import { useEffect, useState } from 'react'

/**
 * Starts a golden-set run and follows it to its end.
 *
 * ## Why this is not simply awaiting the POST
 *
 * It was. A reading case took 35.7 s inside the request, and Bun closes a
 * socket that has sent nothing for ten seconds -- so the browser was handed a
 * bodyless 502 at ten seconds while the server ran happily to completion and
 * wrote the answer to a socket nobody held. The modal said "a requisição
 * falhou (502)" about a run that had worked.
 *
 * The run cannot be made short: it is a model call, and a slower case always
 * exists. So it is started, and then watched.
 *
 * Both sets use this. They differ in what a finished run contains and in
 * nothing else, which is why the shape is generic rather than duplicated --
 * the two screens drifting apart on how a wait looks is exactly what the
 * shared `RunModal` exists to prevent.
 */
const EVERY_MS = 2000

/** What a poll answers, for either set. `result` is whatever that set stores. */
export interface GoldenRun<T> {
  status: 'running' | 'done' | 'failed'
  result?: T | undefined
  error?: string | null | undefined
}

export interface GoldenRunState<T> {
  /** Undefined until the run finishes. */
  result: T | undefined
  /** The run's own failure, or ours in reaching it. */
  error: string | undefined
  busy: boolean
}

export function useGoldenRun<T>(
  /** Fires once, on open, and answers the id to follow. */
  start: () => Promise<{ runId: string }>,
  /** Asks how that run is going. */
  poll: (runId: string) => Promise<GoldenRun<T>>,
  /** Restarts everything when it changes: a modal is opened per case. */
  key: string,
): GoldenRunState<T> {
  const [result, setResult] = useState<T | undefined>()
  const [error, setError] = useState<string | undefined>()

  useEffect(() => {
    let stopped = false
    let timer: ReturnType<typeof setTimeout> | undefined

    const fail = (cause: unknown) => {
      if (!stopped) setError(cause instanceof Error ? cause.message : String(cause))
    }

    const follow = async (runId: string) => {
      if (stopped) return
      try {
        const run = await poll(runId)
        if (stopped) return
        if (run.status === 'done') {
          // A run that finished with nothing is a bug on our side, not an
          // empty result: saying so beats rendering a modal of blanks.
          if (run.result === undefined) setError('a execução terminou sem resultado')
          else setResult(run.result)
          return
        }
        if (run.status === 'failed') {
          setError(run.error ?? 'a execução falhou')
          return
        }
        timer = setTimeout(() => void follow(runId), EVERY_MS)
      } catch (cause) {
        /*
         * A failed poll ends the wait rather than retrying.
         *
         * The run itself is unaffected -- it is recorded, and the sweep closes
         * it if its container went away -- but a modal that silently retries a
         * 404 forever is indistinguishable from one that is still working.
         */
        fail(cause)
      }
    }

    start()
      .then(({ runId }) => follow(runId))
      .catch(fail)

    return () => {
      stopped = true
      if (timer !== undefined) clearTimeout(timer)
    }
    // One run per opening: the modal is opened, watched and closed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  return { result, error, busy: result === undefined && error === undefined }
}
